/**
 * Machine display-label helpers.
 *
 * Product decision (2026-07): the "Vehicle Number" custom field is shown next to the
 * machine name everywhere (lists, pickers, logsheet queues, work-order tables, monthly).
 * Format is `Name (Vehicle Number)`. Where a machine code was previously shown in
 * parentheses, the vehicle number replaces it; the code (then the bare name) is the
 * fallback when a machine has no vehicle number recorded.
 *
 * The backend denormalizes the value onto every machine-bearing DTO as `vehicleNumber`
 * (see `MachineCustomFields.ExtractVehicleNumber`). Only the machine-detail endpoint
 * exposes the raw custom-field bag, so the detail page reads it via
 * {@link vehicleNumberFromCustomFields} instead.
 */

/** The fixed custom-field key that stores a machine's vehicle registration number. */
export const VEHICLE_NUMBER_KEY = 'vehicle_number';

/**
 * Formats a machine's display label as `Name (Vehicle Number)`.
 *
 * @param name           the machine's display name (required)
 * @param vehicleNumber  the denormalized `vehicleNumber` field, if present
 * @param code           the machine code — used as a fallback ONLY for call sites that
 *                       previously rendered the code inline in parentheses. Omit it for
 *                       call sites where the code lives in its own column/element.
 */
export function formatMachineName(
  name: string,
  vehicleNumber?: string | null,
  code?: string | null,
): string {
  const vn = vehicleNumber?.trim();
  if (vn) return `${name} (${vn})`;
  const c = code?.trim();
  if (c) return `${name} (${c})`;
  return name;
}

/** JSON-serializable scalar shape stored per-key in a machine's custom-field bag. */
type CustomFieldScalar = string | number | boolean | null;

/**
 * Pulls the vehicle number out of a machine-detail `customFieldValues` bag (the only
 * response that carries the raw custom fields). Returns `null` when absent or blank.
 */
export function vehicleNumberFromCustomFields(
  values: Readonly<Record<string, CustomFieldScalar>> | undefined | null,
): string | null {
  const raw = values?.[VEHICLE_NUMBER_KEY];
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s === '' ? null : s;
}
